import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import instance from '../api'
import { getApiErrorMessage } from '../apiError'
import { useAuthContext } from '../../context/auth/auth'

/**
 * The dedicated super-admin action (`organization/views.py`
 * `update_subscription_end_date`), which writes `current_period_end` on the
 * organisation's current subscription and returns the detail payload.
 *
 * This used to PATCH the plain detail endpoint. `get_serializer_class` hands
 * anything that is not `list` or `retrieve` the bare `OrganizationSerializer`,
 * a ModelSerializer over a model with no such field, so DRF dropped the key
 * and answered `200` unchanged - the toast claimed a save that never happened.
 *
 * Refusals: 403 for a non-super-admin, 400 for a missing or invalid date or an
 * end at or before the period start, 404 when there is no current subscription.
 */
const subscriptionEndDateEndpoint = (organizationId: string) =>
  `/organizations/organization/${organizationId}/update-subscription-end-date/`

const updateOrganizationEod = async (
  organizationId: string,
  subscriptionEndDate: string,
  token: string,
) => {
  return instance.patch(
    subscriptionEndDateEndpoint(organizationId),
    { subscription_end_date: subscriptionEndDate },
    { headers: { Authorization: `Bearer ${token}` } },
  )
}

const useUpdateOrganizationEod = (organizationId: string) => {
  const { token } = useAuthContext()
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: (subscriptionEndDate: string) =>
      updateOrganizationEod(organizationId, subscriptionEndDate, token!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['organization', organizationId],
      })
      await queryClient.invalidateQueries({ queryKey: ['organizations'] })
      toast.success(t('subscriptionEodUpdated'))
    },
    onError: (error) => {
      // Each refusal names something the operator can act on - the wrong role,
      // a date before the period start, an organisation with no subscription.
      toast.error(
        getApiErrorMessage(error) ?? t('failedToUpdateSubscriptionEod'),
      )
    },
  })
}

export default useUpdateOrganizationEod
